/**
 * Event delegation. Templates never contain inline handlers (that keeps a strict Content-Security-Policy
 * possible). Instead they carry data attributes and one set of listeners on `document` dispatches them:
 *
 *   <button data-act="cart.add" data-id="7">          click      -> actions['cart.add'](el, event)
 *   <input  data-input="search.type">                 input      -> actions['search.type'](el, event)
 *   <select data-change="filter.set">                 change
 *   <form   data-submit="auth.login">                 submit (default prevented)
 *   <input  data-enter="chat.send">                   Enter key
 *   <a href="/student/dashboard">                     same-site links navigate without a page load
 *
 * Handlers may be async. While one runs, the triggering control is disabled and marked aria-busy so a
 * double click cannot send the request twice. A thrown error goes to the central error reporter.
 */
import { navigate } from './router.js';
import { reportError } from './errors.js';

const actions = new Map();

/** Register handlers: register({ 'auth.login': (el, ev) => {...} }). Names must be unique. */
export function register(map) {
  for (const [name, fn] of Object.entries(map)) {
    if (actions.has(name)) throw new Error(`Duplicate action "${name}"`);
    actions.set(name, fn);
  }
}

export function hasAction(name) {
  return actions.has(name);
}

async function run(name, el, event) {
  const fn = actions.get(name);
  if (!fn) {
    reportError(new Error(`No handler registered for "${name}"`), { silent: true });
    return;
  }
  if (el.getAttribute('aria-busy') === 'true') return;
  try {
    const result = fn(el, event);
    if (result && typeof result.then === 'function') {
      const lockable = el.matches('button, [role=button], input[type=submit]');
      if (lockable) {
        el.setAttribute('aria-busy', 'true');
        if ('disabled' in el) el.disabled = true;
      }
      try {
        await result;
      } finally {
        if (lockable && el.isConnected) {
          el.removeAttribute('aria-busy');
          if ('disabled' in el) el.disabled = false;
        }
      }
    }
  } catch (error) {
    reportError(error);
  }
}

function isModifiedClick(event) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

function onClick(event) {
  const actEl = event.target.closest?.('[data-act]');
  if (actEl && !actEl.disabled) {
    if (actEl.tagName === 'A') event.preventDefault();
    run(actEl.dataset.act, actEl, event);
    return;
  }
  const link = event.target.closest?.('a[href]');
  if (!link || isModifiedClick(event) || link.target === '_blank' || link.hasAttribute('download') || link.hasAttribute('data-native')) return;
  const url = new URL(link.href, location.href);
  if (url.origin !== location.origin) return;
  if (url.pathname.startsWith('/api/')) return;
  event.preventDefault();
  navigate(url.pathname + url.search + url.hash);
}

function onInput(event) {
  const el = event.target.closest?.('[data-input]');
  if (el) run(el.dataset.input, el, event);
}

function onChange(event) {
  const el = event.target.closest?.('[data-change]');
  if (el) run(el.dataset.change, el, event);
}

function onSubmit(event) {
  const form = event.target.closest?.('form[data-submit]');
  if (!form) return;
  event.preventDefault();
  run(form.dataset.submit, form, event);
}

function onKeydown(event) {
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    const el = event.target.closest?.('[data-enter]');
    if (el && el.tagName !== 'TEXTAREA') {
      event.preventDefault();
      run(el.dataset.enter, el, event);
      return;
    }
    // Cards and rows that act like buttons: Enter / Space activates them.
    const card = event.target.closest?.('[data-act][tabindex]');
    if (card && card === event.target) {
      event.preventDefault();
      run(card.dataset.act, card, event);
    }
  } else if (event.key === ' ' && event.target.matches?.('[data-act][tabindex]:not(button):not(input)')) {
    event.preventDefault();
    run(event.target.dataset.act, event.target, event);
  }
}

let installed = false;

export function installEvents(root = document) {
  if (installed) return;
  installed = true;
  root.addEventListener('click', onClick);
  root.addEventListener('input', onInput);
  root.addEventListener('change', onChange);
  root.addEventListener('submit', onSubmit);
  root.addEventListener('keydown', onKeydown);
}
